
import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuthContext } from '../../components/feature/AuthProvider';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  
  const { user } = useAuthContext();
  const navigate = useNavigate();

  // Redirect if already logged in - check admin status first
  if (user) {
    const adminEmails = [
      'carlos@driveninsurance.com', 
      'andrew@driveninsurance.com',
      'pj@driveninsurance.com',
      'jeremy@driveninsurance.com'
    ];
    
    const isAdmin = adminEmails.includes(user.email?.toLowerCase() || '');
    
    if (isAdmin) {
      return <Navigate to="/dashboard" replace />;
    } else {
      return <Navigate to="/employee-dashboard" replace />;
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate email and password are filled
    if (!email || !password) {
      setError('Please enter both email and password.');
      setIsLoading(false);
      return;
    }

    // Show disclaimer popup instead of logging in directly
    setIsLoading(false);
    setShowDisclaimer(true);
  };

  const handleDisclaimerAgree = async () => {
    setIsLoading(true);
    setShowDisclaimer(false);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (signInError) {
        setError('Invalid email or password. Please try again.');
        setIsLoading(false);
        return;
      }

      if (data.user) {
        // Define admin emails - only these users can access main dashboard
        const adminEmails = [
          'carlos@driveninsurance.com', 
          'andrew@driveninsurance.com',
          'pj@driveninsurance.com',
          'jeremy@driveninsurance.com'
        ];
        
        // Check if user is admin
        const isAdmin = adminEmails.includes(email.toLowerCase());
        
        if (isAdmin) {
          navigate('/dashboard');
        } else {
          // For all non-admin users (including PH Retention), redirect to employee dashboard
          navigate('/employee-dashboard');
        }
      }
    } catch (err) {
      setError('Login failed. Please check your credentials.');
      setIsLoading(false);
    }
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimer(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setResetMessage('');

    try {
      // Use the current origin instead of hardcoding localhost
      const redirectUrl = `${window.location.origin}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: redirectUrl,
      });

      if (error) {
        setError(error.message);
      } else {
        setResetMessage('Password reset email sent! Please check your inbox.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetMessage('');
          setResetEmail('');
        }, 3000);
      }
    } catch (error) {
      setError('Failed to send reset email. Please try again.');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-400 via-green-500 to-green-600 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo Section */}
        <div className="text-center mb-8 mt-12">
          <div className="bg-white rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center shadow-lg overflow-hidden">
            <img 
              src="https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/6dd6a14ade16f41073966e110936f6bc.jfif"
              alt="Driven Insurance Services"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Driven Insurance</h1>
          <p className="text-green-100">Dashboard</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {!showForgotPassword ? (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h2>
                <p className="text-gray-600">Please sign in to access your dashboard</p>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
                  <i className="ri-error-warning-line text-red-500 mr-2"></i>
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <i className="ri-user-line text-gray-400"></i>
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-5 focus:border-transparent"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <i className="ri-lock-line text-gray-400"></i>
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 hover:text-gray-600 transition-colors`}></i>
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </>
                  ) : (
                    <>
                      <i className="ri-login-circle-line mr-2"></i>
                      Sign In
                    </>
                  )}
                </button>
              </form>

              {/* Forgot Password Link */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-green-600 hover:text-green-700 font-medium transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Reset Password</h2>
                <p className="text-gray-600">Enter your email to receive a reset link</p>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
                  <i className="ri-error-warning-line text-red-500 mr-2"></i>
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              )}

              {resetMessage && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  <span className="text-green-700 text-sm">{resetMessage}</span>
                </div>
              )}

              <form onSubmit={handleForgotPassword} className="space-y-6">
                <div>
                  <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <i className="ri-mail-line text-gray-400"></i>
                    </div>
                    <input
                      id="resetEmail"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line mr-2"></i>
                      Send Reset Link
                    </>
                  )}
                </button>
              </form>

              {/* Back to Login Link */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setError('');
                    setResetMessage('');
                    setResetEmail('');
                  }}
                  className="text-sm text-gray-600 hover:text-gray-700 font-medium transition-colors"
                >
                  ← Back to Sign In
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-green-100 text-sm">
            © 2013 Driven Insurance Services. All rights reserved. V2.0
          </p>
        </div>
      </div>

      {/* Disclaimer Popup */}
      {showDisclaimer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <i className="ri-shield-check-line text-green-600 text-2xl mr-3"></i>
                <h3 className="text-xl font-bold text-gray-900">System Access Agreement</h3>
              </div>
              
              <div className="text-sm text-gray-700 space-y-4 max-h-96 overflow-y-auto">
                <p className="font-medium">
                  Access to this system is restricted to authorized users only. By logging in, you agree to the following terms regarding confidential and proprietary information:
                </p>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Confidential Information</h4>
                  <p>
                    You acknowledge that all data, information, files, and other material contained within this system are the exclusive and confidential property of Driven Insurance Services. This includes, but is not limited to, trade secrets, customer information, financial data, and other sensitive materials.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Permitted Use</h4>
                  <p>
                    You agree to use the information contained in this system solely for the authorized business purposes of Driven Insurance Services. Any other use, including for personal gain or outside of your employment/authorization, is strictly prohibited.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Prohibition on Disclosure</h4>
                  <p>
                    You agree not to disclose, share, distribute, or otherwise disseminate any confidential information to unauthorized individuals or entities. This obligation of confidentiality continues even after your authorization to access this system has terminated.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Security Responsibilities</h4>
                  <p>
                    You are responsible for maintaining the security of your account and login credentials. You must create a strong, secure password and never share your username or password with anyone else.
                  </p>
                  <p className="mt-2">
                    You must not leave an active session unattended. You agree to log out of your account when your work is complete.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">System Monitoring</h4>
                  <p>
                    You consent to the monitoring of all activities on this system. All system access, user actions, and data on this system may be recorded, reviewed, copied, audited, and inspected by authorized personnel.
                  </p>
                  <p className="mt-2">
                    Evidence of unauthorized use or criminal activity may be disclosed to law enforcement.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Violation of Terms</h4>
                  <p>
                    Any unauthorized access, use, or disclosure of confidential information is strictly prohibited and may result in disciplinary action, termination of employment, and legal prosecution to the full extent of the law.
                  </p>
                </div>

                <p className="font-medium text-gray-900">
                  By proceeding to log in, you are affirming your agreement to these terms and conditions.
                </p>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleDisclaimerCancel}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisclaimerAgree}
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </>
                  ) : (
                    'I Agree'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
